import React from "react";
import {
    TextField,
    Select,
    MenuItem,
    Button,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
    Collapse,
    Badge,
    Chip,
    Typography,
} from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import SearchIcon from "@material-ui/icons/Search";
import {TransactionStatus} from "../model/AdminDashboardTypes";

interface Filters {
    loanNumber: string;
    transactionStatus: TransactionStatus | "";
    paymentIntentId: string;
    chargeId: string;
    since: string;
    until: string;
    brandSlug: string;
}

const FILTER_LABELS: Record<keyof Filters, string> = {
    loanNumber: "Loan #",
    transactionStatus: "Status",
    paymentIntentId: "PI ID",
    chargeId: "Charge ID",
    since: "Initiated since",
    until: "Initiated until",
    brandSlug: "Brand",
};

interface Props {
    filters: Filters;
    /** Populated by the parent. Empty until the brand list loads, or if it fails. */
    brands: { slug: string; displayName: string }[];
    onFilterChange: (filters: Filters) => void;
    onApply: () => void;
    onClear: () => void;
}

interface State {
    expanded: boolean;
}

export class ReceiptLogFilters extends React.Component<Props, State> {
    state: State = {expanded: true};

    private getActiveFilters = (): { key: keyof Filters; label: string; value: string }[] => {
        const {filters} = this.props;
        const active: { key: keyof Filters; label: string; value: string }[] = [];
        (Object.keys(FILTER_LABELS) as (keyof Filters)[]).forEach((key) => {
            if (filters[key]) {
                active.push({key, label: FILTER_LABELS[key], value: String(filters[key])});
            }
        });
        return active;
    };

    private handleChange = (field: keyof Filters) => (
        event: React.ChangeEvent<{ value: unknown }>
    ) => {
        this.props.onFilterChange({
            ...this.props.filters,
            [field]: event.target.value as string,
        });
    };

    private handleRemoveFilter = (field: keyof Filters) => () => {
        const updated = {...this.props.filters, [field]: ""};
        this.props.onFilterChange(updated);
        // Auto-apply when removing a chip
        setTimeout(() => this.props.onApply(), 0);
    };

    private handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            this.props.onApply();
        }
    };

    private toggleExpanded = () => {
        this.setState((prev) => ({expanded: !prev.expanded}));
    };

    private formatChipValue = (key: keyof Filters, value: string): string => {
        if (key === "since" || key === "until") {
            const d = new Date(value);
            return d.toLocaleString("en-US", {
                month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            });
        }
        if (value.length > 16) return value.slice(0, 6) + "\u2026" + value.slice(-6);
        return value;
    };

    render() {
        const {filters, onApply, onClear} = this.props;
        const {expanded} = this.state;
        const activeFilters = this.getActiveFilters();
        const activeCount = activeFilters.length;

        return (
            <div className="receipt-log-filters-wrapper">
                <div className="receipt-log-filters-toggle">
                    <Tooltip title={expanded ? "Hide filters" : "Show filters"}>
                        <IconButton size="small" onClick={this.toggleExpanded}>
                            <Badge badgeContent={activeCount} color="primary" invisible={activeCount === 0}>
                                <FilterListIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                    <Typography variant="body2" color="textSecondary" className="receipt-log-filters-title">
                        Filters
                    </Typography>
                    {!expanded && activeCount > 0 && (
                        <div className="receipt-log-filters-chips">
                            {activeFilters.map((f) => (
                                <Chip
                                    key={f.key}
                                    label={`${f.label}: ${this.formatChipValue(f.key, f.value)}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onDelete={this.handleRemoveFilter(f.key)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <Collapse in={expanded}>
                    <div className="receipt-log-filters" onKeyDown={this.handleKeyDown}>
                        <div className="receipt-log-filters__row">
                            <TextField
                                label="Loan Number"
                                value={filters.loanNumber}
                                onChange={this.handleChange("loanNumber")}
                                variant="outlined"
                                size="small"
                                placeholder="e.g. 12345"
                            />
                            <FormControl variant="outlined" size="small" className="receipt-log-filters__status">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.transactionStatus}
                                    onChange={this.handleChange("transactionStatus")}
                                    label="Status"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="INITIATED">Initiated</MenuItem>
                                    <MenuItem value="PROCESSING">Processing</MenuItem>
                                    <MenuItem value="PAID">Paid</MenuItem>
                                    <MenuItem value="FAILED">Failed</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl variant="outlined" size="small" className="receipt-log-filters__status">
                                <InputLabel>Brand</InputLabel>
                                <Select
                                    value={filters.brandSlug}
                                    onChange={this.handleChange("brandSlug")}
                                    label="Brand"
                                >
                                    {/* A slug is an identifier from a fixed list and the backend
                                        matches it exactly, so this is a picker rather than a text
                                        box — typing "vista" would silently match nothing. */}
                                    <MenuItem value="">All</MenuItem>
                                    {this.props.brands.map((b) => (
                                        <MenuItem key={b.slug} value={b.slug}>{b.displayName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <div className="receipt-log-filters__date-group">
                                <TextField
                                    label="Initiated since"
                                    type="datetime-local"
                                    value={filters.since}
                                    onChange={this.handleChange("since")}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{shrink: true}}
                                />
                                <span className="receipt-log-filters__date-separator">&ndash;</span>
                                <TextField
                                    label="Initiated until"
                                    type="datetime-local"
                                    value={filters.until}
                                    onChange={this.handleChange("until")}
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{shrink: true}}
                                />
                            </div>
                        </div>
                        <div className="receipt-log-filters__row">
                            <TextField
                                label="Payment Intent ID"
                                value={filters.paymentIntentId}
                                onChange={this.handleChange("paymentIntentId")}
                                variant="outlined"
                                size="small"
                                placeholder="pi_..."
                            />
                            <TextField
                                label="Charge ID"
                                value={filters.chargeId}
                                onChange={this.handleChange("chargeId")}
                                variant="outlined"
                                size="small"
                                placeholder="ch_..."
                            />
                            <div className="receipt-log-filters__actions">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onApply}
                                    startIcon={<SearchIcon />}
                                    size="small"
                                    disableElevation
                                >
                                    Search
                                </Button>
                                <Tooltip title="Clear all filters">
                                    <span>
                                        <Button
                                            variant="outlined"
                                            onClick={onClear}
                                            startIcon={<ClearAllIcon />}
                                            size="small"
                                            disabled={activeCount === 0}
                                        >
                                            Clear
                                        </Button>
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                        {activeCount > 0 && (
                            <div className="receipt-log-filters__active-chips">
                                {activeFilters.map((f) => (
                                    <Chip
                                        key={f.key}
                                        label={`${f.label}: ${this.formatChipValue(f.key, f.value)}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onDelete={this.handleRemoveFilter(f.key)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </Collapse>
            </div>
        );
    }
}
